/**
 * My Quizzes Page
 *
 * View, manage, and launch saved quiz configurations.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MainLayout } from "@/components/layout/MainLayout";
import { db } from "@/db/database";
import type { QuizConfig, Category } from "@/db/types";
import { Plus, Calendar, ListOrdered } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { testIndexedDB } from "@/utils/testDb";
import { PublishQuizDialog } from "@/components/quiz/PublishQuizDialog";
import { BookMascot } from "@/components/ui/BookMascot";
import { QuizCard } from "@/components/quiz/QuizCard";

type SortOption = "lastUsed" | "created" | "title";
type FilterOption = "all" | Category;

export function MyQuizzesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [quizzes, setQuizzes] = useState<QuizConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>("lastUsed");
  const [filterBy, setFilterBy] = useState<FilterOption>("all");
  const [publishingQuiz, setPublishingQuiz] = useState<QuizConfig | null>(null);

  // Load quizzes from database
  useEffect(() => {
    let mounted = true;

    const loadQuizzes = async () => {
      try {
        console.log("📚 Loading quizzes from database...");
        setIsLoading(true);

        // Test IndexedDB support first
        console.log("Testing IndexedDB support...");
        const dbTest = await testIndexedDB();
        console.log("IndexedDB test result:", dbTest);

        if (!dbTest.supported) {
          throw new Error("IndexedDB is not supported in this browser");
        }

        if (!dbTest.canCreate) {
          throw new Error(`IndexedDB cannot create databases: ${dbTest.error || "Unknown reason"}`);
        }

        // Test if database can open
        console.log("Testing database connection...");
        try {
          await db.open();
          console.log("✅ Database opened successfully");
        } catch (openError) {
          console.error("❌ Failed to open database:", openError);
          throw new Error("Database failed to open: " + (openError instanceof Error ? openError.message : "Unknown error"));
        }

        // Add timeout to prevent hanging
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error("Database query timeout")), 5000);
        });

        console.log("Executing query...");
        const allQuizzes = await Promise.race([
          db.quiz_configs.toArray(),
          timeoutPromise,
        ]);

        console.log("✅ Loaded quizzes:", allQuizzes.length);

        if (mounted) {
          setQuizzes(allQuizzes);
          setLoadError(null);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("❌ Failed to load quizzes:", error);
        if (mounted) {
          setIsLoading(false);
          setLoadError(error instanceof Error ? error.message : "Unknown error");
        }
      }
    };

    loadQuizzes();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Filter quizzes
  const filteredQuizzes = quizzes.filter((quiz) => {
    if (filterBy === "all") return true;
    return quiz.category === filterBy;
  });

  // Sort quizzes
  const sortedQuizzes = [...filteredQuizzes].sort((a, b) => {
    switch (sortBy) {
      case "lastUsed":
        return new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime();
      case "created":
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case "title":
        return a.title.localeCompare(b.title);
      default:
        return 0;
    }
  });

  // Handle delete
  const handleDelete = async (quiz: QuizConfig) => {
    if (!confirm(`Delete "${quiz.title}"? This cannot be undone.`)) {
      return;
    }

    try {
      await db.quiz_configs.delete(quiz.signature);
      setQuizzes((prev) => prev.filter((q) => q.signature !== quiz.signature));
      toast({
        title: "Quiz deleted",
        description: `"${quiz.title}" has been removed.`,
      });
    } catch (error) {
      console.error("Failed to delete quiz:", error);
      toast({
        title: "Failed to delete quiz",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  // Category info
  const getCategoryInfo = (category: Category) => {
    switch (category) {
      case "phone":
        return { emoji: "📞", label: "Phone Area Codes", color: "bg-blue-500/10 text-blue-700" };
      case "cities":
        return { emoji: "🏙️", label: "Cities", color: "bg-purple-500/10 text-purple-700" };
      case "admin":
        return { emoji: "🗺️", label: "Administrative", color: "bg-green-500/10 text-green-700" };
      case "custom":
        return { emoji: "📁", label: "Custom", color: "bg-gray-500/10 text-gray-700" };
      default:
        return { emoji: "📁", label: "Custom", color: "bg-gray-500/10 text-gray-700" };
    }
  };

  return (
    <MainLayout
      title="My Quizzes"
      subtitle={`${filteredQuizzes.length} saved quiz${filteredQuizzes.length === 1 ? "" : "es"}`}
      actions={
        <Button onClick={() => navigate("/quiz/studio")}>
          <Plus className="w-4 h-4 mr-2" />
          Create New Quiz
        </Button>
      }
    >
      <div className="container mx-auto p-6 space-y-8">
        {/* Filters and Sort */}
        <section className="rounded-3xl border border-border/70 bg-white/80 p-6 shadow-xl shadow-primary/10 backdrop-blur dark:bg-secondary/70">
          <div className="flex flex-col gap-6">
            {/* Filters Row */}
            <div className="flex flex-wrap gap-6">
              {/* Category Filter */}
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-foreground/50">
                  Category
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={filterBy === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilterBy("all")}
                    className="rounded-xl"
                  >
                    All
                  </Button>
                  <Button
                    variant={filterBy === "phone" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilterBy("phone")}
                    className="rounded-xl"
                  >
                    📞 Phone
                  </Button>
                  <Button
                    variant={filterBy === "admin" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilterBy("admin")}
                    className="rounded-xl"
                  >
                    🗺️ Admin
                  </Button>
                  <Button
                    variant={filterBy === "cities" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilterBy("cities")}
                    className="rounded-xl"
                  >
                    🏙️ Cities
                  </Button>
                  <Button
                    variant={filterBy === "custom" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilterBy("custom")}
                    className="rounded-xl"
                  >
                    📁 Custom
                  </Button>
                </div>
              </div>

              {/* Sort Options */}
              <div className="space-y-2 ml-auto">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-foreground/50">
                  Sort By
                </p>
                <div className="flex gap-2">
                  <Button
                    variant={sortBy === "lastUsed" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSortBy("lastUsed")}
                    className="rounded-xl"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Recent
                  </Button>
                  <Button
                    variant={sortBy === "title" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSortBy("title")}
                    className="rounded-xl"
                  >
                    <ListOrdered className="w-4 h-4 mr-2" />
                    A-Z
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Loading State */}
        {isLoading && !loadError && (
          <div className="text-center py-12 space-y-4">
            <BookMascot size="lg" />
            <p className="text-muted-foreground">Loading quizzes...</p>
          </div>
        )}

        {/* Error State */}
        {loadError && (
          <Card className="border-destructive">
            <CardContent className="p-12 text-center space-y-4">
              <div className="text-6xl">⚠️</div>
              <h2 className="text-2xl font-semibold text-destructive">Database Error</h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                {loadError}
              </p>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  This usually means IndexedDB is corrupted or blocked.
                </p>
                <div className="flex gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setLoadError(null);
                      setIsLoading(true);
                      window.location.reload();
                    }}
                  >
                    Retry
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      if (
                        confirm(
                          "⚠️ This will clear IndexedDB storage. Continue?\n\nNote: You may need to do this manually in browser settings."
                        )
                      ) {
                        try {
                          // Try multiple methods to clear the database
                          console.log("Attempting to delete database...");

                          // Method 1: Dexie delete
                          try {
                            await db.delete();
                            console.log("✅ Database deleted via Dexie");
                          } catch (e) {
                            console.error("Dexie delete failed:", e);
                          }

                          // Method 2: Direct IndexedDB delete
                          try {
                            await new Promise((resolve, reject) => {
                              const request = indexedDB.deleteDatabase("MapQuizzrDB");
                              request.onsuccess = () => {
                                console.log("✅ Database deleted via IndexedDB");
                                resolve(undefined);
                              };
                              request.onerror = () => reject(request.error);
                              request.onblocked = () => reject(new Error("Delete blocked"));
                            });
                          } catch (e) {
                            console.error("IndexedDB delete failed:", e);
                          }

                          toast({
                            title: "Database cleared",
                            description: "Refreshing page...",
                          });
                          setTimeout(() => window.location.reload(), 1000);
                        } catch (error) {
                          console.error("Failed to reset database:", error);
                          alert(
                            "⚠️ Automatic reset failed.\n\n" +
                            "Please manually clear IndexedDB:\n" +
                            "1. Open DevTools (F12)\n" +
                            "2. Go to Application tab\n" +
                            "3. Find IndexedDB → MapQuizzrDB\n" +
                            "4. Right-click and delete\n" +
                            "5. Refresh the page"
                          );
                        }
                      }
                    }}
                  >
                    Clear Database
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!isLoading && !loadError && sortedQuizzes.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="p-12 text-center space-y-4">
              <BookMascot size="xl" />
              <h2 className="text-2xl font-semibold">No quizzes yet</h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                {filterBy === "all"
                  ? "Create your first quiz to get started!"
                  : `No ${getCategoryInfo(filterBy).label} quizzes found.`}
              </p>
              <Button onClick={() => navigate("/quiz/studio")} size="lg">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Quiz
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quiz Grid */}
        {!isLoading && !loadError && sortedQuizzes.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedQuizzes.map((quiz) => (
              <QuizCard
                key={quiz.signature}
                quiz={quiz}
                onShare={(q) => setPublishingQuiz(q)}
                onEdit={async (q) => {
                  try {
                    // Import the cloning utilities
                    const { loadQuizForEditing } = await import("@/utils/quizCloning");
                    const { loadConfigForEditing } = await import("@/stores/useStudioStore");

                    // Load the quiz config and convert it for editing
                    const wizardState = await loadQuizForEditing(q.signature);

                    if (wizardState) {
                      // Load into studio store (will clone by default)
                      loadConfigForEditing(wizardState, q.signature, true);

                      // Navigate to studio
                      navigate("/quiz/studio");
                    } else {
                      toast({
                        title: "Failed to load quiz",
                        description: "Could not load quiz for editing",
                        variant: "destructive",
                      });
                    }
                  } catch (error) {
                    console.error("Failed to load quiz for editing:", error);
                    toast({
                      title: "Error",
                      description: error instanceof Error ? error.message : "Unknown error",
                      variant: "destructive",
                    });
                  }
                }}
                onDelete={handleDelete}
                showActions={true}
              />
            ))}
          </div>
        )}
      </div>

      {/* Publish Quiz Dialog */}
      {publishingQuiz && (
        <PublishQuizDialog
          isOpen={true}
          onClose={() => setPublishingQuiz(null)}
          quizConfig={publishingQuiz}
        />
      )}
    </MainLayout>
  );
}
